import { useState } from 'react';
import type { Greenhouse } from '../../services/greenhousesApi';
import { useCounties } from '@/shared/hooks/useMetaData';
import { useUpdateGreenhouse } from '../../hooks/useGreenhouses';
import { useConfirm, withConfirmSuccess } from '@/shared/contexts/confirm-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Field, FieldLabel } from '@/components/ui/field';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Pencil } from 'lucide-react';
import { UserSearchSelect } from '@/shared/components/UserSearchSelect';
import type { UserSearchResult } from '@/features/auth/services/authApi';
// import { UserSearchSelect } from './UserSearchSelect';

const STRUCTURE_TYPES = [
  'Glass',
  'Polycarbonate',
  'Polyethylene',
  'Shade Cloth',
  'Mixed',
];

interface EditGreenhouseDialogProps {
  greenhouse: Greenhouse;
}

export const EditGreenhouseDialog = ({
  greenhouse,
}: EditGreenhouseDialogProps) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: greenhouse.id,
    greenhouseName: greenhouse.greenhouseName,
    location: greenhouse.location,
    description: greenhouse.description,
    sizeSquareMeters: greenhouse.sizeSquareMeters ?? 0,
    structureType: greenhouse.structureType,
    maxCapacity: greenhouse.maxCapacity ?? 0,
    hasIrrigation: greenhouse.hasIrrigation,
    hasClimateControl: greenhouse.hasClimateControl,
    hasAutomation: greenhouse.hasAutomation,
    isActive: greenhouse.isActive,
    commissionedDate: greenhouse.commissionedDate?.split('T')[0] ?? '',
    managerUserId: greenhouse.managerUserId,
  });
  const [managerName, setManagerName] = useState(
    greenhouse.managerUserName
      ? `${greenhouse.managerUserName} (${greenhouse.managerEmail})`
      : ''
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const updateMutation = useUpdateGreenhouse(greenhouse.id);
  const confirm = useConfirm();
  const countiesQuery = useCounties();

  const handleChange = (
    field: string,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleManagerSelect = (user: UserSearchResult | null) => {
    handleChange("managerUserId", user?.id || "");
    setManagerName(user ? `${user.firstName} ${user.otherNames}` : "");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (!formData.managerUserId) {
      setFieldErrors({ managerUserId: ['Please select a manager'] });
      return;
    }

    const payload = {
      ...formData,
      commissionedDate: new Date(formData.commissionedDate).toISOString(),
    };

    confirm({
      title: "Save greenhouse changes?",
      description: `Changes to “${greenhouse.greenhouseName}” will be saved.`,
      confirmLabel: "Save changes",
      onConfirm: () =>
        withConfirmSuccess(
          updateMutation.mutateAsync(payload as any),
          "Greenhouse updated"
        )
          .then(() => setOpen(false))
          .catch((error: any) => {
            if (error.response?.data?.errors) {
              setFieldErrors(error.response.data.errors);
            }
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start">
          <Pencil className="w-4 h-4" />
          Edit
        </button>
      </DialogTrigger>
      <DialogContent 
                className="max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]">
        <DialogHeader>
          <DialogTitle>Edit Greenhouse</DialogTitle>
          <DialogDescription>
            Update greenhouse information
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {updateMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {updateMutation.error.message}
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="greenhouseName">Greenhouse Name</FieldLabel>
                <Input
                  id="greenhouseName"
                  value={formData.greenhouseName}
                  onChange={(e) =>
                    handleChange('greenhouseName', e.target.value)
                  }
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="location">Location</FieldLabel>
                {countiesQuery.isLoading ? (
                  <div className="h-10 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <Select
                    value={formData.location}
                    onValueChange={(v) => handleChange('location', v)}
                    disabled={updateMutation.isPending}
                  >
                    <SelectTrigger id="location">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countiesQuery.data?.map((county) => (
                        <SelectItem key={county.code} value={county.name}>
                          {county.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="structureType">Structure Type</FieldLabel>
                <Select
                  value={formData.structureType}
                  onValueChange={(v) => handleChange('structureType', v)}
                  disabled={updateMutation.isPending}
                >
                  <SelectTrigger id="structureType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STRUCTURE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel htmlFor="isActive">Status</FieldLabel>
                <div className="flex items-center space-x-2 mt-2">
                  <Checkbox
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) =>
                      handleChange('isActive', checked)
                    }
                    disabled={updateMutation.isPending}
                  />
                  <Label htmlFor="isActive" className="cursor-pointer">
                    Active
                  </Label>
                </div>
              </Field>

              <Field className="col-span-2">
                <FieldLabel htmlFor="description">Description</FieldLabel>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  disabled={updateMutation.isPending}
                  className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </Field>
            </div>
          </div>

          {/* Physical Specifications */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Physical Specifications</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="sizeSquareMeters">Size (m²)</FieldLabel>
                <Input
                  id="sizeSquareMeters"
                  type="number"
                  step="0.01"
                  value={formData.sizeSquareMeters}
                  onChange={(e) =>
                    handleChange('sizeSquareMeters', parseFloat(e.target.value) || 0)
                  }
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="maxCapacity">Max Capacity</FieldLabel>
                <Input
                  id="maxCapacity"
                  type="number"
                  value={formData.maxCapacity}
                  onChange={(e) =>
                    handleChange('maxCapacity', parseInt(e.target.value) || 0)
                  }
                  disabled={updateMutation.isPending}
                />
              </Field>
            </div>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Features</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasIrrigation"
                  checked={formData.hasIrrigation}
                  onCheckedChange={(checked) =>
                    handleChange('hasIrrigation', checked)
                  }
                  disabled={updateMutation.isPending}
                />
                <Label htmlFor="hasIrrigation" className="cursor-pointer">
                  Has Irrigation
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasClimateControl"
                  checked={formData.hasClimateControl}
                  onCheckedChange={(checked) =>
                    handleChange('hasClimateControl', checked)
                  }
                  disabled={updateMutation.isPending}
                />
                <Label htmlFor="hasClimateControl" className="cursor-pointer">
                  Has Climate Control
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasAutomation"
                  checked={formData.hasAutomation}
                  onCheckedChange={(checked) =>
                    handleChange('hasAutomation', checked)
                  }
                  disabled={updateMutation.isPending}
                />
                <Label htmlFor="hasAutomation" className="cursor-pointer">
                  Has Automation
                </Label>
              </div>
            </div>
          </div>

          {/* Manager & Date */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Management</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              {/* <UserSearchSelect
                selectedUserId={formData.managerUserId}
                selectedUserName={managerName}
                onSelect={handleManagerSelect}
                disabled={updateMutation.isPending}
                label="Manager"
                required
              /> */}
              <UserSearchSelect
                selectedUserId={formData.managerUserId}
                selectedUserName={managerName}
                onSelect={handleManagerSelect}
                disabled={updateMutation.isPending}
                label="Manager"
                required
              />
              <Field>
                <FieldLabel htmlFor="commissionedDate">Commissioned Date</FieldLabel>
                <DatePicker
                  id="commissionedDate"
                  value={formData.commissionedDate}
                  onChange={(value) => handleChange('commissionedDate', value)}
                  disabled={updateMutation.isPending}
                />
              </Field>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};